import { memo, useEffect, useMemo, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { WwLoaderFill } from '../../shared/components/WwLoader'
import { previewOf, type ComposeRecipe, type Rect, type TextLine } from './pictureOps'
import type { MadeWith } from './MadeWith'
import { BrushControls, Window, useBrush, type BrushPatch } from './brushStudio'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * Edit text (Huanrui, 2026-10-07): change the words on a picture.
 *
 * Wire a picture in and press Detect: every line of text on it is read
 * (Gemini, a fraction of a cent) and listed, the words as printed on the
 * left and a box on the right with the same words in it. Change the ones
 * that should say something else -- or empty one to take it away -- and
 * press Apply.
 *
 * Apply sends the part of the picture round the changed lines to Image 2.5
 * Sunburst (high, 'auto'), with every change named and every other line in
 * that part listed to be left alone; lines far apart go as separate parts
 * (pictureOps.ts, planText). The answers are lined up with the original and
 * only the changed lines let through, so nothing else on the picture moves.
 * The new picture appears to the right of the card, with the loader on it
 * while it is made.
 *
 * AN AREA (Huanrui, 2026-10-07). Area, beside Detect, shows the picture in
 * the card; a box dragged on it limits the reading to what is inside, and
 * Detect waits until there is one. The rest is the same: the lines found are
 * placed on the whole picture, and Apply works on the whole picture. Area
 * again puts the picture away and reads all of it once more.
 *
 * FINE-TUNE (Huanrui, 2026-10-07). Once there is a result, Fine-tune beside
 * Apply opens a window onto it with the Edit area's brushes (brushStudio.tsx):
 * Eraser shows the original, Restore the new words. Several parts are stepped
 * through one at a time. Update picture puts it together again, in place of
 * the picture Apply made -- nothing goes to the model. Both Apply and Update
 * fold the card, which lets go of its full-size pictures.
 */

/** A picture Apply made, and what it was made from, saved so it can be fine-tuned later. */
export interface TextBlend {
  frame: string
  parts: {
    drawn: { store: 'library' | 'project'; id: string }
    recipe: ComposeRecipe
    /** The painted mask, once Update picture has kept one. */
    mask?: { store: 'library' | 'project'; id: string }
  }[]
  made: MadeWith
}

/** What Fine-tune's window draws from, held by the board in memory while it is open. */
export interface TextStudio {
  source: HTMLCanvasElement | null
  patches: BrushPatch[]
  version: number
}

/** An Apply still being made: saved with the board, so a reload carries on. */
export interface TextPending {
  /** The picture on the board the result goes into. */
  frame: string
  parts: { runId: string; recipe: ComposeRecipe }[]
  made: MadeWith
  /** Parts that could not be started, said once the rest are done. */
  missed?: number
}

export interface TextNodeData extends Record<string, unknown> {
  skillRef?: string
  name?: string
  glyph?: string
  glow?: string
  open?: boolean
  /** What was read, and what each line should say. */
  lines?: TextLine[]
  /** Which picture, turned which way, the lines were read from. */
  readFor?: string
  /** Area mode: the part to read, as fractions of the turned picture; null while it is being chosen. */
  area?: Rect | null
  detecting?: boolean
  applying?: boolean
  runs?: string[]
  error?: string
  credits?: number
  madeCount?: number
  pending?: TextPending
  blend?: TextBlend
  /** Fine-tune is open (not saved). */
  tuning?: boolean
  /** Update picture is putting it together (not saved). */
  updating?: boolean

  /* handed in by the board, never stored */
  /** The picture wired in now, the same way `readFor` names one; '' when none. */
  pictureKey?: string
  /** Its small copy and how it is turned, for choosing an area. */
  picture?: { url: string; turn: number } | null
  wired?: number
  /** About what Apply costs, and in how many parts. */
  estimate?: number
  areas?: number
  live?: number
  maxLive?: number
  onEdit?: (patch: Partial<TextNodeData>) => void
  onDetect?: () => void
  onApply?: () => void
  studio?: TextStudio | null
  onUpdate?: () => void
}

/**
 * The picture, turned as shown, fitted into the card; a box dragged on it is
 * the area to read. In fractions of the picture, so the board's zoom does not
 * matter.
 */
function AreaPicker({
  url,
  turn,
  area,
  onArea,
}: {
  url: string
  turn: number
  area: Rect | null
  onArea: (area: Rect) => void
}) {
  const [drawn, setDrawn] = useState<HTMLCanvasElement | null>(null)
  const [failed, setFailed] = useState(false)
  const [dragging, setDragging] = useState<Rect | null>(null)
  const from = useRef<{ x: number; y: number } | null>(null)
  const surface = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    let gone = false
    setFailed(false)
    // The board's copy, at most 1600 pixels across (2026-10-26): enough to read
    // and to drag a box on; Detect and Apply read the full-size picture.
    previewOf(url, turn)
      .then((canvas) => {
        if (!gone) setDrawn(canvas)
      })
      .catch((error: unknown) => {
        console.warn('[WonderWorks] Edit text: the picture could not be shown', error)
        if (!gone) setFailed(true)
      })
    return () => {
      gone = true
    }
  }, [url, turn])

  useEffect(() => {
    const target = surface.current
    if (!target || !drawn) return
    target.width = drawn.width
    target.height = drawn.height
    target.getContext('2d')?.drawImage(drawn, 0, 0)
  }, [drawn])

  const at = (event: React.PointerEvent) => {
    const r = surface.current!.getBoundingClientRect()
    return {
      x: Math.min(1, Math.max(0, (event.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (event.clientY - r.top) / r.height)),
    }
  }
  const boxOf = (a: { x: number; y: number }, b: { x: number; y: number }): Rect => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  })

  if (failed) return <div className="tnode__empty">The picture could not be shown</div>
  if (!drawn) {
    return (
      <div className="tnode__waiting tnode__waiting--short">
        <WwLoaderFill />
      </div>
    )
  }
  const shown = dragging ?? area
  return (
    <div className="tnode__picker">
      <span
        className="tnode__picture nodrag nopan"
        onPointerDown={(event) => {
          if (event.button !== 0) return
          event.stopPropagation()
          event.preventDefault()
          event.currentTarget.setPointerCapture(event.pointerId)
          from.current = at(event)
          setDragging({ ...from.current, w: 0, h: 0 })
        }}
        onPointerMove={(event) => {
          if (!from.current) return
          setDragging(boxOf(from.current, at(event)))
        }}
        onPointerUp={(event) => {
          if (!from.current) return
          const box = boxOf(from.current, at(event))
          from.current = null
          setDragging(null)
          if (box.w > 0.01 && box.h > 0.01) onArea(box)
        }}
      >
        <canvas ref={surface} className="tnode__canvas" />
        {shown && shown.w > 0 && shown.h > 0 ? (
          <span
            className="tnode__area"
            style={{
              left: `${shown.x * 100}%`,
              top: `${shown.y * 100}%`,
              width: `${shown.w * 100}%`,
              height: `${shown.h * 100}%`,
            }}
          />
        ) : null}
      </span>
    </div>
  )
}

export const TextNode = memo(function TextNode({ data, selected }: NodeProps) {
  const card = data as TextNodeData
  const isOpen = card.open !== false
  const edit = (patch: Partial<TextNodeData>) => card.onEdit?.(patch)
  const lines = card.lines ?? []
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)
  const working = card.applying === true || mine > 0
  // Pressed and not yet answered, so a second press does not start a second run.
  const [starting, setStarting] = useState(false)
  useEffect(() => {
    if (!working) setStarting(false)
  }, [working])
  useEffect(() => {
    if (card.error) setStarting(false)
  }, [card.error])

  const picture = Boolean(card.pictureKey)
  // Area mode is on while `area` is set, or null (on, nothing chosen yet).
  const choosing = card.area !== undefined
  const area = card.area ?? null
  const stale = lines.length > 0 && picture && card.readFor !== card.pictureKey
  const changes = lines.filter((line) => line.to.trim() !== line.text.trim()).length

  /* ---------- Fine-tune ---------- */
  const blend = card.blend
  const blendKey = blend?.parts[0]
    ? `${blend.parts[0].recipe.source.store}:${blend.parts[0].recipe.source.id}:${blend.parts[0].recipe.turn}`
    : ''
  const canTune = Boolean(blend && blend.parts.length > 0 && blendKey === card.pictureKey) && !working
  const tuning = card.tuning === true && canTune
  const studio = card.studio ?? null
  const patches = useMemo(() => studio?.patches ?? [], [studio])
  const brush = useBrush(patches, studio?.version)
  const [part, setPart] = useState(0)
  const parts = blend?.parts ?? []
  const shownPart = Math.min(part, Math.max(0, parts.length - 1))
  const recipe = parts[shownPart]?.recipe
  /** The part being looked at, with room round it, as on the Edit area. */
  const near = recipe
    ? (() => {
        const b = recipe.box
        const m = Math.max(b.w, b.h) * 0.3
        return { x: b.x - m, y: b.y - m, w: b.w + 2 * m, h: b.h + 2 * m }
      })()
    : null
  const ready = Boolean(studio?.source) && patches.length === parts.length
  const updating = card.updating === true

  const why = !picture
    ? 'Connect a picture first'
    : (card.wired ?? 0) > 1
      ? 'One picture at a time — take the extra wires off'
      : lines.length === 0
        ? 'Detect the text first'
        : stale
          ? 'The picture has changed — detect again'
          : changes === 0
            ? 'Change at least one line'
            : full
              ? `${live} already going, here or elsewhere — wait for one to finish`
              : ''

  const setLine = (index: number, to: string) =>
    edit({ lines: lines.map((line, at) => (at === index ? { ...line, to } : line)) })

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'tnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        picture ? 'cnode--lit' : '',
        working ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <div className="tnode__top">
            <button
              type="button"
              className={`tnode__detect tnode__detect--area nodrag${choosing ? ' tnode__detect--on' : ''}`}
              disabled={!picture || card.detecting === true || working}
              // Off again: the area goes, and the whole picture is read.
              onClick={() => edit({ area: choosing ? undefined : null })}
              title={choosing ? 'Read the whole picture instead' : 'Read only part of the picture'}
              aria-pressed={choosing}
            >
              Area
            </button>
            <button
              type="button"
              className="tnode__detect nodrag"
              disabled={!picture || card.detecting === true || working || (choosing && !area)}
              onClick={() => card.onDetect?.()}
              title={
                !picture
                  ? 'Connect a picture first'
                  : choosing && !area
                    ? 'Drag a box on the picture first'
                    : choosing
                      ? 'Read the text inside the box'
                      : 'Read every line of text on the picture'
              }
            >
              {card.detecting ? 'Detecting…' : lines.length > 0 ? 'Detect again' : 'Detect text'}
            </button>
          </div>
          {choosing && card.picture ? (
            <>
              <span className="pnode__label">{area ? 'Area · drag again to change it' : 'Drag a box round the text to read'}</span>
              <AreaPicker
                url={card.picture.url}
                turn={card.picture.turn}
                area={area}
                onArea={(next) => edit({ area: next })}
              />
            </>
          ) : null}
          {stale ? <p className="tnode__note">The picture has changed since this was read — detect again.</p> : null}

          {card.detecting ? (
            <div className="tnode__waiting">
              <WwLoaderFill label="Reading" size="large" />
            </div>
          ) : lines.length > 0 ? (
            <>
              <div className="tnode__heads" aria-hidden="true">
                <span>On the picture</span>
                <span>Should say</span>
              </div>
              <div className="tnode__lines nowheel">
                {lines.map((line, index) => {
                  const differs = line.to.trim() !== line.text.trim()
                  return (
                    <div key={index} className={`tnode__line${differs ? ' tnode__line--changed' : ''}`}>
                      <span className="tnode__from" title={line.text}>
                        {line.text}
                      </span>
                      <span className="tnode__arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="tnode__to-wrap">
                        <input
                          className="tnode__to nodrag"
                          value={line.to}
                          placeholder="(take it away)"
                          disabled={working}
                          onChange={(event) => setLine(index, event.target.value)}
                          aria-label={`New text for ${line.text}`}
                        />
                        {differs ? (
                          <button
                            type="button"
                            className="tnode__undo nodrag"
                            disabled={working}
                            onClick={() => setLine(index, line.text)}
                            title="Back to what is printed"
                            aria-label="Back to what is printed"
                          >
                            ×
                          </button>
                        ) : null}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="tnode__empty">{picture ? 'Press Detect to read the text' : 'Connect a picture'}</div>
          )}

          <div className="tnode__actions">
          <button
            type="button"
            className="cnode__run pnode__run nodrag"
            onClick={() => {
              setStarting(true)
              card.onApply?.()
            }}
            disabled={starting || working || Boolean(why)}
            title={why || 'Redraw the changed lines; everything else stays as it is'}
          >
            {working ? (
              'Applying…'
            ) : starting ? (
              'Starting…'
            ) : (
              <>
                Apply
                {changes > 0 ? <span className="pnode__credits">{changes} changed</span> : null}
                {changes > 0 && card.estimate ? (
                  <span className="pnode__credits">
                    · about {card.estimate} credits{(card.areas ?? 1) > 1 ? ` in ${card.areas} parts` : ''}
                  </span>
                ) : null}
              </>
            )}
          </button>
            <button
              type="button"
              className={`tnode__tune nodrag${tuning ? ' tnode__tune--on' : ''}`}
              disabled={!canTune || updating}
              onClick={() => edit({ tuning: !tuning })}
              aria-pressed={tuning}
              title={
                canTune
                  ? 'Touch up the last result with the Eraser and Restore brushes'
                  : 'Apply first — then the result can be touched up here'
              }
            >
              Fine-tune
            </button>
          </div>

          {tuning ? (
            <>
              <span className="pnode__label tnode__partbar">
                <span>Result · Eraser shows the original, Restore the new</span>
                {parts.length > 1 ? (
                  <span className="tnode__parts">
                    <button
                      type="button"
                      className="tnode__step nodrag"
                      disabled={shownPart === 0}
                      onClick={() => setPart(shownPart - 1)}
                      aria-label="The part before"
                    >
                      ‹
                    </button>
                    {shownPart + 1}/{parts.length}
                    <button
                      type="button"
                      className="tnode__step nodrag"
                      disabled={shownPart >= parts.length - 1}
                      onClick={() => setPart(shownPart + 1)}
                      aria-label="The next part"
                    >
                      ›
                    </button>
                  </span>
                ) : null}
              </span>
              {ready ? (
                <Window
                  source={studio!.source}
                  frame={near}
                  render={(ctx, scale) => brush.render(ctx, scale, recipe?.boxes ?? (recipe ? [recipe.box] : []))}
                  stamp={`${brush.stamp}|${JSON.stringify(recipe?.boxes ?? recipe?.box ?? null)}`}
                  overlay={updating ? undefined : brush.overlay}
                  onLeft={updating ? undefined : brush.startStroke}
                  cursor={updating ? 'default' : 'none'}
                  onHover={updating ? undefined : brush.setHover}
                  sizing={updating ? undefined : brush.sizing}
                />
              ) : (
                <div className="tnode__waiting tnode__waiting--window">
                  <WwLoaderFill label="Loading" size="large" />
                </div>
              )}
              {ready ? (
                <BrushControls brush={brush}>
                  <button
                    type="button"
                    className="enode__apply"
                    disabled={updating}
                    onClick={() => card.onUpdate?.()}
                    title="Put the picture together again with what is painted here, in place of the one Apply made"
                  >
                    {updating ? 'Updating…' : 'Update picture'}
                  </button>
                </BrushControls>
              ) : null}
            </>
          ) : null}

          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {!isOpen && working ? (
        <button type="button" className="cnode__run pnode__run nodrag" disabled title="Applying">
          Applying…
        </button>
      ) : null}

      {!working && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
